import { toast } from '@/components/shared/toasts'
import { useEffect } from 'react'
import { isAxiosError } from 'axios'

interface GlobalErrorHandlerOptions {
  enabled?: boolean
  customHandler?: (error: unknown) => void
  excludeStatusCodes?: number[]
}

export function useGlobalErrorHandler(options: GlobalErrorHandlerOptions = {}) {
  const { enabled = true, customHandler, excludeStatusCodes = [401] } = options

  useEffect(() => {
    if (!enabled) return

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason

      // Skip non-Axios errors
      if (!isAxiosError(error)) return

      // Skip errors marked as handled by mutations
      if (
        error &&
        typeof error === 'object' &&
        (error as any).__handledByMutation
      ) {
        return
      }

      // Skip excluded status codes
      const status = error.response?.status
      if (status && excludeStatusCodes.includes(status)) return

      // Handle the error
      if (customHandler) {
        customHandler(error)
      } else {
        toast.apiError(error, 'Une erreur est survenue')
      }

      // Prevent default browser error handling
      event.preventDefault()
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () =>
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  }, [enabled, customHandler, excludeStatusCodes])

  return {
    handleError: (error: unknown, customMessage?: string) => {
      if (customMessage) {
        toast.error(customMessage)
      } else {
        toast.apiError(error, 'Une erreur est survenue')
      }
    },
  }
}
